import React, { useState } from 'react';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';

const MONTHLY_PRICE_ID        = 'price_1TDCJKRta59KZz9Uj6SwbNnE';
const YEARLY_PRICE_ID         = 'price_1TDCJLRta59KZz9UH4fWGh2w';
// Agency plan – replace with real Stripe Price IDs after creating them in the dashboard
const AGENCY_MONTHLY_PRICE_ID = process.env.REACT_APP_AGENCY_MONTHLY_PRICE_ID || 'price_AGENCY_MONTHLY_PLACEHOLDER';
const AGENCY_YEARLY_PRICE_ID  = process.env.REACT_APP_AGENCY_YEARLY_PRICE_ID  || 'price_AGENCY_YEARLY_PLACEHOLDER';

async function startCheckout(priceId, email) {
  const res = await fetch('/.netlify/functions/create-checkout', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ priceId, email }),
  });
  const data = await res.json();
  if (data.url) window.location.href = data.url;
}

export default function PaywallPage({ user, onTestBypass }) {
  const [billing, setBilling]     = useState('monthly');
  const [loading, setLoading]     = useState(null); // 'standard-monthly' | 'standard-yearly' | 'agency-monthly' | 'agency-yearly'

  const handleCheckout = async (plan, type) => {
    const key = `${plan}-${type}`;
    setLoading(key);
    let priceId;
    if (plan === 'agency') {
      priceId = type === 'monthly' ? AGENCY_MONTHLY_PRICE_ID : AGENCY_YEARLY_PRICE_ID;
    } else {
      priceId = type === 'monthly' ? MONTHLY_PRICE_ID : YEARLY_PRICE_ID;
    }
    await startCheckout(priceId, user.email);
    setLoading(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 px-6 h-14 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-indigo-600 rounded-lg flex items-center justify-center">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M3 2L13 8L3 14V2Z" fill="white"/></svg>
          </div>
          <span className="font-semibold text-sm tracking-tight text-gray-900">FrameIt</span>
        </div>
        <button
          onClick={() => signOut(auth)}
          className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1.5 transition-colors"
        >
          <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
            <path d="M5 2H2a1 1 0 00-1 1v8a1 1 0 001 1h3M9 10l3-3-3-3M13 7H5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Abmelden
        </button>
      </header>

      <div className="flex-1 flex flex-col items-center justify-center px-6 py-16">
        {/* Lock icon */}
        <div className="w-14 h-14 bg-indigo-100 rounded-2xl flex items-center justify-center mb-5">
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
            <rect x="3" y="10" width="16" height="11" rx="2" stroke="#6366f1" strokeWidth="1.5"/>
            <path d="M7 10V7a4 4 0 018 0v3" stroke="#6366f1" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </div>

        <h1 className="text-2xl font-semibold text-gray-900 mb-2 text-center">
          Kein aktives Abo
        </h1>
        <p className="text-sm text-gray-400 text-center max-w-sm mb-10">
          Hey <strong className="text-gray-600">{user.displayName || user.email}</strong> – um FrameIt zu nutzen, brauchst du ein Abo. Starte jetzt 7 Tage kostenlos.
        </p>

        {/* Toggle */}
        <div className="flex items-center gap-1.5 bg-gray-100 rounded-xl p-1 mb-8">
          <button
            onClick={() => setBilling('monthly')}
            className={`px-4 py-2 text-xs font-semibold rounded-lg transition-all ${billing === 'monthly' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
          >Monatlich</button>
          <button
            onClick={() => setBilling('yearly')}
            className={`px-4 py-2 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 ${billing === 'yearly' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Jährlich
            <span className="bg-emerald-100 text-emerald-700 text-xs px-1.5 py-0.5 rounded-full font-semibold">–17%</span>
          </button>
        </div>

        {/* Plan cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 w-full max-w-3xl">
          {/* Standard Monthly */}
          <div className={`bg-white rounded-2xl border-2 p-5 transition-all ${billing === 'monthly' ? 'border-indigo-600 shadow-lg shadow-indigo-100' : 'border-gray-100'}`}>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">Standard · Monatlich</p>
            <div className="flex items-end gap-1 mb-4">
              <span className="text-3xl font-bold text-gray-900">9,99</span>
              <span className="text-gray-400 text-sm mb-0.5">€/Mo</span>
            </div>
            <ul className="space-y-1.5 text-sm text-gray-500 mb-5">
              {['Unbegrenzte Projekte', 'Kunden-Links', 'Feedback & Kommentare', 'Cloud-Speicher', '7 Tage gratis'].map(f => (
                <li key={f} className="flex items-center gap-2">
                  <svg width="12" height="12" viewBox="0 0 14 14" fill="none"><path d="M2 7L5.5 10.5L12 3.5" stroke="#6366f1" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  <span className="text-xs">{f}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={() => handleCheckout('standard', 'monthly')}
              disabled={!!loading}
              className="w-full py-2.5 bg-indigo-600 text-white text-sm font-semibold rounded-xl hover:bg-indigo-700 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
            >
              {loading === 'standard-monthly' && <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>}
              Gratis starten
            </button>
          </div>

          {/* Standard Yearly */}
          <div className={`bg-white rounded-2xl border-2 p-5 transition-all relative ${billing === 'yearly' ? 'border-indigo-600 shadow-lg shadow-indigo-100' : 'border-gray-100'}`}>
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-500 text-white text-xs font-bold px-3 py-0.5 rounded-full whitespace-nowrap">
              Beliebt · spare 20 €
            </div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">Standard · Jährlich</p>
            <div className="flex items-end gap-1 mb-1">
              <span className="text-3xl font-bold text-gray-900">99,99</span>
              <span className="text-gray-400 text-sm mb-0.5">€/Jahr</span>
            </div>
            <p className="text-xs text-gray-400 mb-3">= 8,33 € / Monat</p>
            <ul className="space-y-1.5 text-sm text-gray-500 mb-5">
              {['Unbegrenzte Projekte', 'Kunden-Links', 'Feedback & Kommentare', 'Cloud-Speicher', '7 Tage gratis', 'Prioritäts-Support'].map(f => (
                <li key={f} className="flex items-center gap-2">
                  <svg width="12" height="12" viewBox="0 0 14 14" fill="none"><path d="M2 7L5.5 10.5L12 3.5" stroke="#10b981" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  <span className="text-xs">{f}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={() => handleCheckout('standard', 'yearly')}
              disabled={!!loading}
              className="w-full py-2.5 bg-indigo-600 text-white text-sm font-semibold rounded-xl hover:bg-indigo-700 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
            >
              {loading === 'standard-yearly' && <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>}
              Gratis starten
            </button>
          </div>

          {/* Agency Plan */}
          <div className="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-2xl border-2 border-purple-500 p-5 transition-all relative shadow-lg shadow-purple-200">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-purple-500 text-white text-xs font-bold px-3 py-0.5 rounded-full whitespace-nowrap flex items-center gap-1">
              <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M6 1l1.5 3H11l-2.5 2 1 3L6 7.5 2.5 9l1-3L1 4h3.5L6 1z" fill="white"/></svg>
              Agentur-Plan
            </div>
            <p className="text-xs font-semibold text-purple-200 uppercase tracking-widest mb-2">Agency · Monatlich</p>
            <div className="flex items-end gap-1 mb-4">
              <span className="text-3xl font-bold text-white">29,99</span>
              <span className="text-purple-300 text-sm mb-0.5">€/Mo</span>
            </div>
            <ul className="space-y-1.5 text-sm text-purple-100 mb-5">
              {[
                'Alles aus Standard',
                'KI-Kampagnenplanung',
                'Strategische Rationale',
                'Plakat · Flyer · Skript',
                'Social Media API',
                'LinkedIn · Instagram · Meta',
              ].map(f => (
                <li key={f} className="flex items-center gap-2">
                  <svg width="12" height="12" viewBox="0 0 14 14" fill="none"><path d="M2 7L5.5 10.5L12 3.5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  <span className="text-xs">{f}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={() => handleCheckout('agency', 'monthly')}
              disabled={!!loading}
              className="w-full py-2.5 bg-white text-purple-700 text-sm font-semibold rounded-xl hover:bg-purple-50 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
            >
              {loading === 'agency-monthly' && <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>}
              Agentur starten
            </button>
          </div>
        </div>

        <p className="text-xs text-gray-400 mt-6 text-center">
          Sichere Zahlung via Stripe · Jederzeit kündbar · Kein Risiko
        </p>

        {onTestBypass && (
          <div className="mt-6 flex items-center gap-4">
            <button
              onClick={onTestBypass}
              className="text-xs text-gray-300 hover:text-gray-400 underline underline-offset-2 transition-colors"
            >
              [Test] Standard überspringen
            </button>
            <button
              onClick={() => onTestBypass('agency')}
              className="text-xs text-purple-300 hover:text-purple-400 underline underline-offset-2 transition-colors"
            >
              [Test] Agency überspringen
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
